/**
 * Submit hook for `LegacyEncounterReadOnlyForm`. Uses the legacy-specific
 * `legacyEncounterToBundleInput` adapter to project legacy-typed values
 * back into the flat bundle input.
 *
 * @agentic connections
 */

import { useCallback } from "react";
import { useNavigate } from "react-router";
import { useMedplum, useMedplumProfile } from "@medplum/react";
import type {
  Encounter,
  Patient,
  Practitioner,
  PractitionerRole,
} from "@medplum/fhirtypes";
import type { LegacyEncounterFormData } from "@benchmark/core/types";
import { createEncounterFacade } from "@/facades/encounter.facade";
import { legacyEncounterToBundleInput } from "@benchmark/core/adapters";
import {
  showErrorNotification,
  showSuccessNotification,
  showWarningNotification,
} from "@benchmark/ui/utils";

/**
 * Options for {@link useLegacyEncounterSubmit}.
 *
 * @agentic connections
 */
export type UseLegacyEncounterSubmitOptions = {
  patient: Patient | undefined;
  existingEncounter?: Encounter;
  originalFormData?: LegacyEncounterFormData;
  practitionerRoles?: PractitionerRole[];
  onSuccess?: (encounterId: string) => void;
  onSubmitted?: () => void;
};

/**
 * Returns a submit handler for `LegacyEncounterReadOnlyForm`. Legacy
 * encounters are edit-only; `existingEncounter` is always set when this
 * hook is invoked.
 *
 * @agentic connections
 */
export const useLegacyEncounterSubmit = ({
  patient,
  existingEncounter,
  originalFormData,
  practitionerRoles,
  onSuccess,
  onSubmitted,
}: UseLegacyEncounterSubmitOptions) => {
  const medplum = useMedplum();
  const profile = useMedplumProfile() as Practitioner | undefined;
  const navigate = useNavigate();

  return useCallback(
    async (formData: LegacyEncounterFormData) => {
      if (!profile || !patient) {
        showErrorNotification(
          "Missing required data. Please refresh the page and try again.",
        );
        return;
      }

      const flat = legacyEncounterToBundleInput(formData);
      const originalFlat = originalFormData
        ? legacyEncounterToBundleInput(originalFormData)
        : undefined;

      try {
        const facade = createEncounterFacade(medplum);
        const myRole = (practitionerRoles ?? []).find(
          (role) =>
            role.practitioner?.reference === `Practitioner/${profile.id}`,
        );

        const encounter = await facade.upsert({
          formData: flat,
          patient,
          practitioner: profile,
          practitionerRole: myRole,
          existingEncounter,
          originalFormData: originalFlat,
          onWarning: (msg) => showWarningNotification(msg),
        });

        showSuccessNotification(
          `Encounter ${existingEncounter ? "updated" : "created"} successfully. All clinical data has been saved.`,
        );

        onSubmitted?.();

        if (onSuccess && encounter.id) {
          onSuccess(encounter.id);
        } else {
          navigate(`/Patient/${patient.id}/Encounter/${encounter.id}`)?.catch(
            (error) => {
              console.error(
                "Failed to navigate after legacy encounter save:",
                error,
              );
            },
          );
        }
      } catch (error) {
        const errorMessage =
          error instanceof Error ? error.message : String(error);
        showErrorNotification(errorMessage);
        console.error("Legacy encounter save failed:", error);
      }
    },
    [
      profile,
      patient,
      medplum,
      navigate,
      existingEncounter,
      originalFormData,
      onSuccess,
      onSubmitted,
      practitionerRoles,
    ],
  );
};
